import { Button } from "@/components/ui/button";
import { useApi } from "@/hooks/useApi";
import { useAppForm } from "@/hooks/useForm";
import {
	TableHeader,
	TableRow,
	TableHead,
	TableBody,
	ErrorRow,
	SkeletonRows,
	NoResultsRow,
	Table,
} from "@/components/ui/table";
import { InputRowMemo } from "./ConfigRow";
import type { ConfigReferencia } from "@/@types/Api_Extracted";
import { useMemo } from "react";
import { useTour } from "@reactour/tour";
import { toast } from "react-toastify";
import { useSelector } from "@tanstack/react-form";

export function ConfigTable() {
	const { setCurrentStep, isOpen, setIsOpen } = useTour();

	const api = useApi();
	const queryConfig = api.useQuery("get", "/v1/Empresa/Config/", {});
	const queryBloques = api.useQuery("get", "/v1/Empresa/Config/Bloques", {});
	const queryFactores = api.useQuery("get", "/v1/Empresa/Config/Factores", {});
	const mutateConfig = api.useMutation("post", "/v1/Empresa/Config/");

	const isLoading =
		queryConfig.isLoading ||
		queryBloques.isLoading ||
		queryFactores.isLoading ||
		mutateConfig.isPending;
	const isError =
		queryConfig.isError || queryBloques.isError || queryFactores.isError;

	const myForm = useAppForm({
		defaultValues: {
			rows: queryConfig.data ?? new Array<ConfigReferencia | null>(),
		},
		onSubmit: async (e) => {
			console.log("Submit", e);
			try {
				const rows = e.value.rows
					.filter((row) => row != null)
					.map((row) => {
						// eslint-disable-next-line @typescript-eslint/no-unused-vars	-- Estamos limpiando el objeto para evitar enviar datos innecesarios
						const { factor, ...cleanRow } = row;
						return cleanRow;
					});
				await mutateConfig.mutateAsync({ body: rows });
				toast("Guardado.", { type: "success" });

				await queryConfig.refetch();
				myForm.reset();
			} catch (error) {
				console.error("Error al guardar la configuración:", error);
				toast("Error al guardar la configuración.", { type: "error" });
			}
		},
	});
	const isSuccess = !isLoading && !isError;
	const isEmpty = isSuccess && queryConfig.data?.length === 0;
	const formData = useSelector(myForm.store, (state) => state.values.rows);

	const sortedFactores = useMemo(() => {
		return (
			queryFactores.data?.sort((a, b) => a.nombre.localeCompare(b.nombre)) ?? []
		);
	}, [queryFactores.data]);

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				if (isOpen) {
					setIsOpen(false);
				} else {
					void myForm.handleSubmit(e);
				}
			}}
		>
			<Table className="w-full">
				<TableHeader>
					<TableRow>
						<TableHead data-tour-id="header-factor">Factor</TableHead>
						<TableHead data-tour-id="header-referencia">Referencia</TableHead>
						<TableHead data-tour-id="header-unidad">Unidad</TableHead>
						<TableHead data-tour-id="header-co2">Co2 equivalente</TableHead>
						<TableHead data-tour-id="header-acciones" />
					</TableRow>
				</TableHeader>
				<TableBody className="text-center">
					{isError && (
						<ErrorRow message="Error al cargar la configuración." colSpan={4} />
					)}
					{isLoading && <SkeletonRows rows={10} colSpan={4} />}
					{isEmpty && (
						<NoResultsRow
							message="No se ha creado la configuración"
							colSpan={4}
						/>
					)}

					{isSuccess &&
						formData.map(
							(row, index) =>
								row != null && (
									<InputRowMemo
										key={row.idReferencia}
										index={index}
										form={myForm}
										bloques={queryBloques.data}
										factores={sortedFactores}
									/>
								),
						)}
				</TableBody>
			</Table>

			<div className="flex justify-between mt-4 w-full">
				<myForm.Field name="rows" mode="array">
					{(field) => (
						<Button
							type="button"
							variant="secondary"
							data-tour-id="add-concept-button"
							onClick={() => {
								if (isOpen) {
									setCurrentStep((step) => ++step); // Avanza al siguiente paso del tour
								} else {
									field.pushValue(
										{
											idReferencia: crypto.randomUUID(),
											idFactorMiteco: null,
											idFactorEmpresa: null,
											descripcionCorta: "",

											factor: null,
										},
										// {
										// 	focusName: `table.${fields.length}.factor`,
										// },
									);
								}
							}}
						>
							➕ Añadir concepto
						</Button>
					)}
				</myForm.Field>

				<Button
					type="submit"
					variant="primary"
					disabled={isLoading}
					data-tour-id="submit-button"
				>
					Guardar
				</Button>
			</div>
		</form>
	);
}
